import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { FaPhone, FaEnvelope, FaArrowRight } from 'react-icons/fa';
import logo from '../assets/Logo 1.png';

gsap.registerPlugin(ScrollTrigger);

const Footer = () => {
  const footerRef = useRef(null);

  useEffect(() => {
    gsap.fromTo(
      '.footer-animate',
      { y: 40, opacity: 0 },
      {
        y: 0,
        opacity: 1,
        duration: 0.8,
        stagger: 0.15,
        ease: 'power3.out',
        scrollTrigger: {
          trigger: footerRef.current,
          start: 'top 85%',
        },
      }
    );
  }, []);

  const quickLinks = [
    { label: 'Home', href: '/' },
    { label: 'Loans & Calculator', href: '/costs' },
    { label: 'How It Works', href: '/apply-now' },
    { label: 'FAQs', href: '/faqs' },
  ];

  const legalLinks = [
    // { label: 'Privacy & Credit', href: '/legal/privacy-and-credit' },
    { label: 'Credit Guides', href: '/legal/credit-guides' },
    { label: 'Credit Reporting Statement', href: '/legal/credit-reporting' },
    { label: 'Complaints Management Policy', href: '/legal/complaints' },
    { label: 'Disclaimer', href: '/legal/disclaimer' },
    { label: 'Privacy Statement', href: '/legal/privacy-statement' },
    { label: 'Target Market Determination', href: '/legal/target-market' },
  ];


  return (
    <footer ref={footerRef} className="relative bg-primary overflow-hidden -mt-px">
      {/* Top Wave Decoration - Must be first to appear above */}
      <div className="absolute top-0 left-0 w-full overflow-hidden leading-none z-10" style={{ marginTop: '-1px' }}>
        <svg className="relative block w-full h-10 md:h-12 lg:h-14" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1200 120" preserveAspectRatio="none" style={{ display: 'block', verticalAlign: 'bottom' }}>
          <path d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V0H0V27.35A600.21,600.21,0,0,0,321.39,56.44Z" fill="#ffffff" stroke="none"></path>
        </svg>
      </div>

      {/* Decorative Background Elements */}
      <div className="absolute inset-0 pointer-events-none">
        {/* Gradient overlays */}
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-secondary/10 rounded-full blur-[150px]"></div>
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-secondary/5 rounded-full blur-[120px]"></div>
        
        {/* Floating dollar signs */}
        <motion.div
          animate={{ y: [0, -30, 0], rotate: [0, 15, 0] }}
          transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
          className="absolute top-20 right-[15%] text-[120px] text-secondary/5 font-bold"
        >
          $
        </motion.div>
        <motion.div
          animate={{ y: [0, 20, 0], rotate: [0, -10, 0] }}
          transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut', delay: 1 }}
          className="absolute bottom-40 left-[10%] text-[100px] text-secondary/5 font-bold"
        >
          $
        </motion.div>
      </div>

      <div className="container mx-auto px-4 lg:px-8 pt-20 pb-8 relative z-10">
        {/* Top Section - CTA Card */}
        <div className="footer-animate mb-16">
          <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-secondary via-secondary-400 to-secondary-500 p-8 md:p-12 shadow-2xl">
            {/* Pattern overlay */}
            <div className="absolute inset-0 opacity-10" 
              style={{
                backgroundImage: `radial-gradient(circle at 2px 2px, rgba(4, 93, 61, 0.3) 1px, transparent 0)`,
                backgroundSize: '30px 30px'
              }}
            ></div>
            
            <div className="relative z-10 flex flex-col lg:flex-row items-center justify-between gap-8">
              <div>
                <h3 className="font-display text-3xl md:text-4xl font-bold text-primary-800 mb-3">
                  Ready to Get Started?
                </h3>
                <p className="text-primary-700 text-lg max-w-xl">
                  Apply now and get approved in as little as 24 hours. Quick, simple, and hassle-free.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-4">
                <a
                  href="/apply-now"
                  className="group bg-primary hover:bg-primary-700 text-white font-bold py-4 px-8 rounded-full transition-all duration-300 shadow-xl hover:shadow-2xl flex items-center justify-center gap-2 whitespace-nowrap"
                >
                  Apply Now
                  <FaArrowRight className="group-hover:translate-x-1 transition-transform" />
                </a>
                <a
                  href="/contact-us"
                  className="bg-white/20 backdrop-blur-sm hover:bg-white/30 text-primary-800 border-2 border-primary-800 font-semibold py-4 px-8 rounded-full transition-all duration-300 flex items-center justify-center gap-2 whitespace-nowrap"
                >
                  Contact Us
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Main Footer Content */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 mb-12">
          {/* Company Info */}
          <div className="footer-animate lg:col-span-1">
            <div className="mb-6">
              <img 
                src={logo} 
                alt="AirCash Finance" 
                className="h-16 md:h-20 w-auto max-w-full"
                style={{ 
                  objectFit: 'contain',
                  display: 'block',
                  maxWidth: '320px'
                }}
              />
            </div>
            <p className="text-white/70 leading-relaxed mb-6">
              Fast & Simple Loans – Anytime You Need Cash. Trusted Australian lender with transparent fees and quick approval.
            </p>
            <div className="flex items-center gap-2 text-sm text-white/60">
              <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
              <span>Secure & Encrypted</span>
            </div>
          </div>

          {/* Quick Links */}
          <div className="footer-animate">
            <h4 className="font-display font-bold text-lg text-white mb-6 relative inline-block">
              Quick Links
              <span className="absolute -bottom-2 left-0 w-12 h-1 bg-secondary rounded-full"></span>
            </h4>
            <ul className="space-y-3">
              {quickLinks.map((link, index) => (
                <li key={index}>
                  <a
                    href={link.href}
                    className="text-white/70 hover:text-secondary transition-colors duration-300 flex items-center gap-2 group"
                  >
                    <span className="w-0 group-hover:w-2 h-0.5 bg-secondary rounded-full transition-all duration-300"></span>
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal Links */}
          <div className="footer-animate">
            <h4 className="font-display font-bold text-lg text-white mb-6 relative inline-block">
              Legal
              <span className="absolute -bottom-2 left-0 w-12 h-1 bg-secondary rounded-full"></span>
            </h4>
            <ul className="space-y-3">
              {legalLinks.slice(0, 6).map((link, index) => (
                <li key={index}>
                  <a
                    href={link.href}
                    className="text-white/70 hover:text-secondary transition-colors duration-300 flex items-center gap-2 group text-sm"
                  >
                    <span className="w-0 group-hover:w-2 h-0.5 bg-secondary rounded-full transition-all duration-300"></span>
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div className="footer-animate">
            <h4 className="font-display font-bold text-lg text-white mb-6 relative inline-block">
              Get In Touch
              <span className="absolute -bottom-2 left-0 w-12 h-1 bg-secondary rounded-full"></span>
            </h4>
            <div className="space-y-4">
              <a
                href="tel:0468098248"
                className="flex items-center gap-4 p-4 bg-white/5 hover:bg-white/10 rounded-xl transition-all duration-300 group border border-white/10 hover:border-secondary/50"
              >
                <div className="w-12 h-12 bg-secondary/20 group-hover:bg-secondary rounded-xl flex items-center justify-center transition-colors">
                  <FaPhone className="text-secondary group-hover:text-primary-800" />
                </div>
                <div>
                  <p className="text-white/60 text-xs">Call Us</p>
                  <p className="text-white font-semibold">0468 098 248</p>
                </div>
              </a>

              <a
                href="mailto:info@aircashfinance.com.au"
                className="flex items-center gap-4 p-4 bg-white/5 hover:bg-white/10 rounded-xl transition-all duration-300 group border border-white/10 hover:border-secondary/50"
              >
                <div className="w-12 h-12 bg-secondary/20 group-hover:bg-secondary rounded-xl flex items-center justify-center transition-colors">
                  <FaEnvelope className="text-secondary group-hover:text-primary-800" />
                </div>
                <div>
                  <p className="text-white/60 text-xs">Email Us</p>
                  <p className="text-white font-semibold text-sm break-all">info@aircashfinance.com.au</p>
                </div>
              </a>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="footer-animate border-t border-white/10 pt-8 text-white/50 text-xs md:text-sm">
          {/*<div className="items-center text-center md:text-left"> 
            <p className="text-white/50 text-xs md:text-sm leading-relaxed max-w-2xl">*/}
              © {new Date().getFullYear()} Aircash Finance  ABN 88 692 896 138, Authorised Credit Representative Number 576018, Facilitating Loans Provided by Strawberry Loans Pty Ltd ABN 74 621 474 655, Australian Credit Licence 519 253
            {/* </p>
           <div className="flex items-center gap-6 text-white/50 text-xs">
              <span>Made with ❤️ in Australia</span>
            </div>
          </div>*/}
        </div>
      </div>
    </footer>
  );
};

export default Footer;
